import React, { useState, useEffect, useCallback } from 'react';
import { Skeleton, Alert, Empty, DatePicker, Button, Typography, message } from 'antd';
import {
  DownloadOutlined, PrinterOutlined, CalendarOutlined,
  CheckCircleOutlined, CloseCircleOutlined, ClockCircleOutlined,
  WarningOutlined, SyncOutlined, MinusCircleOutlined,
} from '@ant-design/icons';
import { fetchAnalytics, triggerSync, fetchSyncStatus } from '../services/analyticsApi';
import { exportAnalyticsCSV } from '../services/analyticsExport';
import {
  WeeklyMiniChart, DailyMiniChart, DropoffFunnel, RescheduleFunnel, BookingFunnel, RouteBreakdown,
  WeeklyTable, DailyTable,
  AppointmentTypeBreakdown, ProductCategoryBreakdown, DurationBreakdown,
  GenderBreakdown, LanguageBreakdown, ClinicPerformanceTable,
  TimeOfDayChart, DayOfWeekChart,
} from './CategoryCharts';
import { DeviceBrowserSection } from './DeviceBrowserSection';
import './analytics.css';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
const { Text } = Typography;

// ─── Helpers ─────────────────────────────────────────────────────────────────
const crColor = (cr) => cr >= 30 ? '#1a7f4b' : cr >= 15 ? '#b45309' : '#c0392b';
const crBg    = (cr) => cr >= 30 ? 'rgba(26,127,75,0.08)' : cr >= 15 ? 'rgba(180,83,9,0.08)' : 'rgba(192,57,43,0.08)';

const fmt        = (d) => dayjs(d).format('YYYY-MM-DD');   // API param format
const fmtDisplay = (d) => dayjs(d).format('DD/MM/YYYY');   // UI display format
const today = () => dayjs();
const yesterday = () => dayjs().subtract(1, 'day');
const last7Start  = () => today().subtract(7, 'day');
const last30Start = () => today().subtract(30, 'day');

const formatNumber = (num) => (num == null ? '—' : new Intl.NumberFormat('en-GB').format(num));

// ─── CR Badge ────────────────────────────────────────────────────────────────
const CRBadge = ({ cr }) => (
  <span style={{
    display: 'inline-block', padding: '3px 14px',
    borderRadius: 20, fontSize: 13, fontWeight: 700,
    background: crBg(cr), color: crColor(cr),
    border: `1px solid ${crColor(cr)}30`,
  }}>
    {(+cr).toFixed(1)}% CR
  </span>
);

// ─── Stat Pill ────────────────────────────────────────────────────────────────
const Pill = ({ label, value, color, bg, icon }) => (
  <div style={{ padding: '12px 14px', background: bg, border: `1px solid ${color}25`, borderRadius: 12, minWidth: 0 }}>
    <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color, opacity: 0.8, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
      {icon} {label}
    </div>
    <div style={{ fontSize: 24, fontWeight: 800, color, lineHeight: 1 }}>{formatNumber(value)}</div>
  </div>
);

// ─── Section Label ────────────────────────────────────────────────────────────
const SL = ({ children }) => (
  <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
    {children}
    <div style={{ flex: 1, height: 1, background: '#f0f0f0' }} />
  </div>
);

// ─── Section Header (numbered) ────────────────────────────────────────────────
const SectionHeader = ({ number, label, sublabel, color }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 }}>
    <div style={{ width: 32, height: 32, borderRadius: '50%', background: color, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 800, flexShrink: 0 }}>
      {number}
    </div>
    <div style={{ textAlign: 'left' }}>
      <div style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>{label}</div>
      <div style={{ fontSize: 12, color: '#9ca3af' }}>{sublabel}</div>
    </div>
  </div>
);

// ─── Toolbar ─────────────────────────────────────────────────────────────────
const Toolbar = ({ filters, pickerValue, onRangeChange, onClearRange, isCustomRange, onExport, exporting, onPrint, onSync, isSyncing, lastSyncedAt, fromCache }) => {

  return (
    <div className="ar-toolbar">
      <div className="ar-toolbar-left">
        <CalendarOutlined style={{ color: '#6b7280' }} />
        <Text style={{ fontSize: 13, color: '#374151' }}>Custom Date Range:</Text>
        <RangePicker
          size="middle"
          format="DD/MM/YYYY"
          value={pickerValue}            /* controlled — reset clears the display */
          minDate={dayjs().subtract(30, 'day')}
          maxDate={yesterday()}
          onChange={(dates) => {
            if (dates && dates[0] && dates[1]) {
              onRangeChange(dates, dates[0].format('YYYY-MM-DD'), dates[1].format('YYYY-MM-DD'));
            } else {
              onClearRange();
            }
          }}
          style={{ width: 260 }}
          placeholder={['Start date', 'End date']}
          disabledDate={(current) => {
            const earliest = dayjs().subtract(30, 'day').startOf('day');
            return current && (current > yesterday().endOf('day') || current < earliest);
          }}
        />
        {isCustomRange && (
          <Button size="small" onClick={onClearRange} style={{ fontSize: 12, color: '#6b7280' }}>
            ← Back to default view
          </Button>
        )}
      </div>
      <div className="ar-toolbar-right" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        {fromCache && (
          <span style={{ fontSize: 11, fontWeight: 600, color: '#b45309', background: 'rgba(180,83,9,0.09)', border: '1px solid rgba(180,83,9,0.25)', borderRadius: 12, padding: '2px 10px' }}>
            Cached data
          </span>
        )}
        {lastSyncedAt && (
          <Text style={{ fontSize: 13, color: '#6b7280', marginRight: 8 }}>
            Last synced: {dayjs(lastSyncedAt).format('DD/MM/YYYY HH:mm')}
          </Text>
        )}
        <Button
          size="small"
          icon={<SyncOutlined spin={isSyncing} />}
          onClick={onSync}
          disabled={isSyncing}
        >
          {isSyncing ? 'Syncing...' : 'Sync Data'}
        </Button>
        <Button
          className="ar-export-btn"
          size="small"
          icon={<DownloadOutlined />}
          loading={exporting}
          onClick={onExport}
        >
          Export CSV
        </Button>
        <Button size="small" icon={<PrinterOutlined />} onClick={onPrint}>Print</Button>
      </div>
    </div>
  );
};

// ─── Grand Summary Row ────────────────────────────────────────────────────────
const SummaryRow = ({ data }) => {
  const cats = [
    { label: 'Booking',      total: data.booking.total,       cr: data.booking.cr,      color: '#008385', confirmed: data.booking.confirmed },
    { label: 'Cancellation', total: data.cancelling.total,    cr: data.cancelling.cr,   color: '#c0392b', confirmed: data.cancelling.succeeded },
    { label: 'Rescheduling', total: data.rescheduling.total,  cr: data.rescheduling.cr, color: '#6b45c8', confirmed: data.rescheduling.succeeded },
  ];
  // appointment_update is a new backend metric — include it if present
  const apptUpdate = data.appointment_update ?? null;
  // Use the backend's authoritative total (includes appointment_update sessions).
  // Fall back to summing cats if the field is absent.
  const grandTotal = data.meta?.total_sessions_all_journeys
    ?? cats.reduce((a, c) => a + (c.total || 0), 0);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '18px 24px', background: '#fff', border: '1px solid #f0f0f0', borderRadius: 12, marginBottom: 28, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
      {/* Main journey metrics */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 120 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>Total Sessions</div>
          <div style={{ fontSize: 28, fontWeight: 800, color: '#111827' }}>{formatNumber(grandTotal)}</div>
          <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>Across all journeys</div>
        </div>
        {cats.map((c, i) => (
          <div key={i} style={{ flex: 1, minWidth: 150, borderLeft: '1px solid #f3f4f6', paddingLeft: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: c.color }} />
              <span style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{c.label}</span>
            </div>
            <div style={{ fontSize: 24, fontWeight: 800, color: c.color }}>{formatNumber(c.confirmed)} <span style={{ fontSize: 13, fontWeight: 400, color: '#9ca3af' }}>/ {formatNumber(c.total)}</span></div>
            <div style={{ fontSize: 11, color: crColor(c.cr), fontWeight: 600, marginTop: 2 }}>{c.cr}% success rate</div>
            <div style={{ width: '100%', background: '#f3f4f6', borderRadius: 99, height: 4, marginTop: 6, overflow: 'hidden' }}>
              <div style={{ width: `${grandTotal ? (c.total / grandTotal) * 100 : 0}%`, height: '100%', background: c.color, borderRadius: 99 }} />
            </div>
          </div>
        ))}
        {/* Appointment Update — 4th peer column */}
        {apptUpdate?.total > 0 && (
          <div style={{ flex: 1, minWidth: 150, borderLeft: '1px solid #f3f4f6', paddingLeft: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#e07b39' }} />
              <span style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Appt. Update</span>
            </div>
            <div style={{ fontSize: 24, fontWeight: 800, color: '#e07b39' }}>
              {formatNumber(apptUpdate.succeeded ?? 0)}{' '}
              <span style={{ fontSize: 13, fontWeight: 400, color: '#9ca3af' }}>/ {formatNumber(apptUpdate.total)}</span>
            </div>
            <div style={{ fontSize: 11, color: crColor(apptUpdate.cr), fontWeight: 600, marginTop: 2 }}>{apptUpdate.cr}% success rate</div>
            <div style={{ width: '100%', background: '#f3f4f6', borderRadius: 99, height: 4, marginTop: 6, overflow: 'hidden' }}>
              <div style={{ width: `${grandTotal ? (apptUpdate.total / grandTotal) * 100 : 0}%`, height: '100%', background: '#e07b39', borderRadius: 99 }} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Booking Category Content (reusable for both split and full view) ─────────
const BookingCategoryContent = ({ data, showDaily = true, isSplit = false }) => {
  const d = data.booking;
  const useDailyChart = showDaily && d.daily?.length;
  // Split view (narrow half-width column) → 3 cols so 5 pills wrap into 2 rows (3+2)
  // Full view (custom date range, full width) → 5 cols, all in one row
  const pillCols = isSplit ? 'repeat(3, 1fr)' : 'repeat(5, 1fr)';
  return (
    <>
      {/* KPI Pills */}
      <div style={{ display: 'grid', gridTemplateColumns: pillCols, gap: 10, marginBottom: 24 }}>
        <Pill label="Total Sessions" value={d.total}     color="#374151" bg="#f9fafb"                       icon={<ClockCircleOutlined />} />
        <Pill label="Confirmed"      value={d.confirmed} color="#1a7f4b" bg="rgba(26,127,75,0.07)"         icon={<CheckCircleOutlined />} />
        <Pill label="Expired"        value={d.expired}   color="#c0392b" bg="rgba(192,57,43,0.07)"         icon={<MinusCircleOutlined />} />
        <Pill label="Abandoned"      value={d.abandoned} color="#b45309" bg="rgba(180,83,9,0.07)"          icon={<WarningOutlined />} />
        <Pill label="Failed"         value={d.failed}    color="#c0392b" bg="rgba(192,57,43,0.07)"         icon={<CloseCircleOutlined />} />
      </div>
      {/* Left (7-day): show Daily when available. Right (30-day): always Weekly */}
      {useDailyChart
        ? <DailyMiniChart daily={d.daily} valueKey="booked" totalKey="total" color="#008385" />
        : <WeeklyMiniChart weekly={d.weekly} valueKey="booked" totalKey="total" color="#008385" />}
      <div className="ar-split-divider" />
      <RouteBreakdown by_route={d.by_route} />
      <div className="ar-split-divider" />
      <ProductCategoryBreakdown by_product_category={d.by_product_category} />
      <div className="ar-split-divider" />
      <AppointmentTypeBreakdown by_appointment_type={d.by_appointment_type} />
      <div className="ar-split-divider" />
      <DurationBreakdown by_duration={d.by_duration} />
      <div className="ar-split-divider" />
      <GenderBreakdown by_gender={d.by_gender} />
      <div className="ar-split-divider" />
      <LanguageBreakdown by_language={d.by_language} />
      <div className="ar-split-divider" />
      <ClinicPerformanceTable by_clinic={d.by_clinic} />
      <div className="ar-split-divider" />
      <TimeOfDayChart by_hour_bucket={d.by_hour_bucket} />
      <div className="ar-split-divider" />
      <DayOfWeekChart by_day_of_week={d.by_day_of_week} />
      {/* BookingFunnel hidden — bars don't narrow sequentially so it doesn't read as a funnel.
          Re-enable once redesigned as a proper narrowing funnel visualisation.
      <div className="ar-split-divider" />
      <BookingFunnel funnel={d.funnel} color="#008385" />
      */}
      {/* Left (7-day): DailyTable when available. Right (30-day): always WeeklyTable */}
      {useDailyChart
        ? <DailyTable daily={d.daily} isBooking={true} />
        : <WeeklyTable weekly={d.weekly} isBooking={true} />}
      <div className="ar-split-divider" />
      <SL>Device &amp; Browser</SL>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 200px', minWidth: 200 }}>
          <DeviceBrowserSection title="Device"  data={d.by_device}  color="#008385" />
        </div>
        <div style={{ flex: '1 1 200px', minWidth: 200 }}>
          <DeviceBrowserSection title="Browser" data={d.by_browser} color="#1a7f4b" />
        </div>
      </div>
    </>
  );
};

// ─── CATEGORY 1: Booking (full-width card) ────────────────────────────────────
const BookingCategory = ({ data }) => (
  <div className="ar-card ar-section">
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(0,131,133,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CheckCircleOutlined style={{ color: '#008385', fontSize: 18 }} />
        </div>
        <div style={{ textAlign: 'left' }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>New Booking Journeys</div>
          <div style={{ fontSize: 12, color: '#6b7280' }}>Sessions where users attempted to book a new appointment</div>
        </div>
      </div>
      <CRBadge cr={data.booking.cr} />
    </div>
    <BookingCategoryContent data={data} />
  </div>
);

// ─── Journey Category Content (reusable) ──────────────────────────────────────
const JourneyCategoryContent = ({ data, color, showDaily = true, showFunnel = true, isSplit = false, useRescheduleFunnel = false, showDemographics = true }) => {
  const useDailyChart = showDaily && data.daily?.length;
  // Split view → 2 cols so 4 pills wrap into 2 rows (2+2)
  const pillCols = isSplit ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)';
  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: pillCols, gap: 10, marginBottom: 24 }}>
        <Pill label="Total Journeys" value={data.total}     color="#374151" bg="#f9fafb"              icon={<ClockCircleOutlined />} />
        <Pill label="Completed"      value={data.succeeded} color="#1a7f4b" bg="rgba(26,127,75,0.07)" icon={<CheckCircleOutlined />} />
        <Pill label="Abandoned"      value={data.abandoned} color="#b45309" bg="rgba(180,83,9,0.07)"  icon={<WarningOutlined />} />
        <Pill label="Failed"         value={data.failed}    color="#c0392b" bg="rgba(192,57,43,0.07)" icon={<CloseCircleOutlined />} />
      </div>
      {/* Left (7-day): Daily when available. Right (30-day): always Weekly */}
      {useDailyChart
        ? <DailyMiniChart daily={data.daily} valueKey="succeeded" totalKey="total" color={color} valueLabel="Success Rate" />
        : <WeeklyMiniChart weekly={data.weekly} valueKey="succeeded" totalKey="total" color={color} valueLabel="Success Rate" />}
      <div className="ar-split-divider" />
      <ProductCategoryBreakdown by_product_category={data.by_product_category} />
      <div className="ar-split-divider" />
      <DurationBreakdown by_duration={data.by_duration} />
      {showDemographics && <><div className="ar-split-divider" /><GenderBreakdown by_gender={data.by_gender} /></>}
      {showDemographics && <><div className="ar-split-divider" /><LanguageBreakdown by_language={data.by_language} /></>}
      {showDemographics && <><div className="ar-split-divider" /><ClinicPerformanceTable by_clinic={data.by_clinic} /></>}
      {showFunnel && (
        <>
          <div className="ar-split-divider" />
          {useRescheduleFunnel
            ? <RescheduleFunnel funnel={data.funnel} color={color} />
            : <DropoffFunnel funnel={data.funnel} color={color} />}
        </>
      )}
      <div className="ar-split-divider" />
      {/* Left (7-day): DailyTable when available. Right (30-day): always WeeklyTable */}
      {useDailyChart
        ? <DailyTable daily={data.daily} isBooking={false} />
        : <WeeklyTable weekly={data.weekly} isBooking={false} />}
    </>
  );
};

// ─── CATEGORY 2 & 3: Cancellation / Rescheduling ─────────────────────────────
const JourneyCategory = ({ title, description, data, color, icon, showFunnel = true, useRescheduleFunnel = false, showDemographics = true }) => (
  <div className="ar-card ar-section">
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ width: 36, height: 36, borderRadius: 10, background: `${color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {React.cloneElement(icon, { style: { color, fontSize: 18 } })}
        </div>
        <div style={{ textAlign: 'left' }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>{title}</div>
          <div style={{ fontSize: 12, color: '#6b7280' }}>{description}</div>
        </div>
      </div>
      <CRBadge cr={data.cr} />
    </div>
    <JourneyCategoryContent data={data} color={color} showFunnel={showFunnel} useRescheduleFunnel={useRescheduleFunnel} showDemographics={showDemographics} />
  </div>
);

// ─── Column data renderer (shared by left & right) ──────────────────────────
const ColumnBookingContent = ({ data, color = '#008385', showDaily = true }) => (
  <>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ width: 28, height: 28, borderRadius: 8, background: `${color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CheckCircleOutlined style={{ color, fontSize: 14 }} />
        </div>
        <span style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>New Booking Journeys</span>
      </div>
      <CRBadge cr={data.booking.cr} />
    </div>
    {/* isSplit=true: narrow column → pills wrap into 2 rows */}
    <BookingCategoryContent data={data} showDaily={showDaily} isSplit={true} />
  </>
);

// ─── CATEGORY 4: Appointment Update content (data-driven — only visible sections) ──
// Color brand: #e07b39 (warm amber — distinct from booking/cancel/reschedule)
const AU_COLOR = '#e07b39';

const AppointmentUpdateContent = ({ data, showDaily = true, isSplit = false }) => {
  const hasData = !!data;
  if (!hasData) return null;

  const useDailyChart = showDaily && data.daily?.length;
  // Only show a sub-section when the underlying array/field has actual data
  const hasDuration     = !!data.by_duration?.length;
  const hasGender       = !!data.by_gender?.length;
  const hasLanguage     = !!data.by_language?.length;
  const hasClinic       = !!data.by_clinic?.length;
  const hasHourBucket   = data.by_hour_bucket?.some(b => b.total > 0);
  const hasDayOfWeek    = data.by_day_of_week?.some(d => d.total > 0);

  // Pills: always 4 cols in full-view, 2 cols in split view
  const pillCols = isSplit ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)';

  return (
    <>
      {/* KPI Pills — always visible */}
      <div style={{ display: 'grid', gridTemplateColumns: pillCols, gap: 10, marginBottom: 24 }}>
        <Pill label="Total Sessions" value={data.total}     color="#374151" bg="#f9fafb"              icon={<ClockCircleOutlined />} />
        <Pill label="Updated"        value={data.succeeded} color="#1a7f4b" bg="rgba(26,127,75,0.07)" icon={<CheckCircleOutlined />} />
        <Pill label="Expired"        value={data.expired}   color="#c0392b" bg="rgba(192,57,43,0.07)" icon={<MinusCircleOutlined />} />
        <Pill label="Abandoned"      value={data.abandoned} color="#b45309" bg="rgba(180,83,9,0.07)"  icon={<WarningOutlined />} />
      </div>

      {/* Trend chart */}
      {useDailyChart
        ? <DailyMiniChart  daily={data.daily}   valueKey="succeeded" totalKey="total" color={AU_COLOR} valueLabel="Updated" />
        : <WeeklyMiniChart weekly={data.weekly} valueKey="succeeded" totalKey="total" color={AU_COLOR} valueLabel="Updated" />}

      {/* Conditionally rendered sections */}
      {hasDuration && <><div className="ar-split-divider" /><DurationBreakdown by_duration={data.by_duration} /></>}
      {hasGender   && <><div className="ar-split-divider" /><GenderBreakdown   by_gender={data.by_gender} /></>}
      {hasLanguage && <><div className="ar-split-divider" /><LanguageBreakdown by_language={data.by_language} /></>}
      {hasClinic   && <><div className="ar-split-divider" /><ClinicPerformanceTable by_clinic={data.by_clinic} /></>}
      {hasHourBucket  && <><div className="ar-split-divider" /><TimeOfDayChart by_hour_bucket={data.by_hour_bucket} /></>}
      {hasDayOfWeek   && <><div className="ar-split-divider" /><DayOfWeekChart by_day_of_week={data.by_day_of_week} /></>}

      {/* Trend table */}
      <div className="ar-split-divider" />
      {useDailyChart
        ? <DailyTable  daily={data.daily}   isBooking={false} />
        : <WeeklyTable weekly={data.weekly} isBooking={false} />}
    </>
  );
};

// ─── Split-View Column Wrapper ────────────────────────────────────────────────
const SplitColumn = ({ side, label, sublabel, children }) => (
  <div className="ar-split-col">
    <div className={`ar-split-col-header ${side}`}>
      <span className="ar-split-badge">{label}</span>
      <span className="ar-split-title">{side === 'left' ? 'Last 7 Days' : 'Last 30 Days'}</span>
      <span className="ar-split-sub">{sublabel}</span>
    </div>
    <div className="ar-split-col-body">
      {children}
    </div>
  </div>
);

// ─── Split View (default) ─────────────────────────────────────────────────────
const SplitView = ({ weeklyData, monthlyData, loading7, loading30 }) => {
  const yesterdayFmt = fmtDisplay(yesterday());
  const week7Start   = fmtDisplay(last7Start());
  const month30Start = fmtDisplay(last30Start());

  const renderColumnContent = (data, loading, emptyLabel, showDaily) => {
    if (loading && !data) return <Skeleton active paragraph={{ rows: 12 }} />;
    if (!data)   return <Empty description={emptyLabel} style={{ margin: '40px 0' }} />;
    return <ColumnBookingContent data={data} showDaily={showDaily} />;
  };

  return (
    <>
      {/* Summary row — show only when at least one dataset is fully loaded */}
      {(!loading7 || weeklyData) && weeklyData && <SummaryRow data={monthlyData || weeklyData} />}

      <SectionHeader number="1" label="Booking Journeys" sublabel="New appointment booking sessions" color="#008385" />

      <div className="ar-split-layout" style={{ marginBottom: 28 }}>
        {/* LEFT: last 7 days — daily trend */}
        <SplitColumn side="left" label="Weekly" sublabel={`${week7Start} → ${yesterdayFmt}`}>
          {renderColumnContent(weeklyData, loading7, 'No data for last 7 days', true)}
        </SplitColumn>

        {/* RIGHT: last 30 days — always weekly trend */}
        <SplitColumn side="right" label="Monthly" sublabel={`${month30Start} → ${yesterdayFmt}`}>
          {renderColumnContent(monthlyData, loading30, 'No data for last 30 days', false)}
        </SplitColumn>
      </div>

      {/* Cancellation & Rescheduling — shown side-by-side too */}
      {(!loading7 || !loading30 || weeklyData || monthlyData) && (weeklyData || monthlyData) && (
        <>
          <SectionHeader number="2" label="Cancellation Journeys" sublabel="Sessions where users cancelled an existing booking" color="#c0392b" />
          <div className="ar-split-layout" style={{ marginBottom: 28 }}>
            <SplitColumn side="left" label="Weekly" sublabel={`${week7Start} → ${yesterdayFmt}`}>
              {(loading7 && !weeklyData) ? <Skeleton active paragraph={{ rows: 8 }} /> :
                weeklyData ? <JourneyCategoryContent data={weeklyData.cancelling} color="#c0392b" showDaily={true} showFunnel={false} isSplit={true} showDemographics={false} /> :
                <Empty description="No data" style={{ margin: '20px 0' }} />}
            </SplitColumn>
            <SplitColumn side="right" label="Monthly" sublabel={`${month30Start} → ${yesterdayFmt}`}>
              {(loading30 && !monthlyData) ? <Skeleton active paragraph={{ rows: 8 }} /> :
                monthlyData ? <JourneyCategoryContent data={monthlyData.cancelling} color="#c0392b" showDaily={false} showFunnel={false} isSplit={true} showDemographics={false} /> :
                <Empty description="No data" style={{ margin: '20px 0' }} />}
            </SplitColumn>
          </div>

          <SectionHeader number="3" label="Rescheduling Journeys" sublabel="Sessions where users moved an existing booking to a new slot" color="#6b45c8" />
          <div className="ar-split-layout" style={{ marginBottom: 28 }}>
            <SplitColumn side="left" label="Weekly" sublabel={`${week7Start} → ${yesterdayFmt}`}>
              {(loading7 && !weeklyData) ? <Skeleton active paragraph={{ rows: 8 }} /> :
                weeklyData ? <JourneyCategoryContent data={weeklyData.rescheduling} color="#6b45c8" showDaily={true} isSplit={true} useRescheduleFunnel={true} /> :
                <Empty description="No data" style={{ margin: '20px 0' }} />}
            </SplitColumn>
            <SplitColumn side="right" label="Monthly" sublabel={`${month30Start} → ${yesterdayFmt}`}>
              {(loading30 && !monthlyData) ? <Skeleton active paragraph={{ rows: 8 }} /> :
                monthlyData ? <JourneyCategoryContent data={monthlyData.rescheduling} color="#6b45c8" showDaily={false} isSplit={true} useRescheduleFunnel={true} /> :
                <Empty description="No data" style={{ margin: '20px 0' }} />}
            </SplitColumn>
          </div>

          {/* Section 4 — Appointment Update (only when backend returns data) */}
          {((weeklyData?.appointment_update?.total > 0) || (monthlyData?.appointment_update?.total > 0)) && (
            <>
              <SectionHeader number="4" label="Appointment Update Journeys" sublabel="Sessions where a user updated an existing appointment" color={AU_COLOR} />
              <div className="ar-split-layout" style={{ marginBottom: 28 }}>
                <SplitColumn side="left" label="Weekly" sublabel={`${week7Start} → ${yesterdayFmt}`}>
                  {(loading7 && !weeklyData) ? <Skeleton active paragraph={{ rows: 8 }} /> :
                    weeklyData?.appointment_update
                      ? <AppointmentUpdateContent data={weeklyData.appointment_update} showDaily={true} isSplit={true} />
                      : <Empty description="No data" style={{ margin: '20px 0' }} />}
                </SplitColumn>
                <SplitColumn side="right" label="Monthly" sublabel={`${month30Start} → ${yesterdayFmt}`}>
                  {(loading30 && !monthlyData) ? <Skeleton active paragraph={{ rows: 8 }} /> :
                    monthlyData?.appointment_update
                      ? <AppointmentUpdateContent data={monthlyData.appointment_update} showDaily={false} isSplit={true} />
                      : <Empty description="No data" style={{ margin: '20px 0' }} />}
                </SplitColumn>
              </div>
            </>
          )}
        </>
      )}
    </>
  );
};

// ─── Full-Width Custom Range View ─────────────────────────────────────────────
const FullView = ({ data }) => (
  <>
    <SummaryRow data={data} />
    <SectionHeader number="1" label="Booking Journeys" sublabel="New appointment booking sessions" color="#008385" />
    <BookingCategory data={data} />
    <SectionHeader number="2" label="Cancellation Journeys" sublabel="Sessions where users cancelled an existing booking" color="#c0392b" />
    <JourneyCategory
      title="Cancellation Journeys"
      description="Sessions where a user attempted to cancel a booking"
      data={data.cancelling}
      color="#c0392b"
      icon={<MinusCircleOutlined />}
      showFunnel={false}
      showDemographics={false}
    />
    <SectionHeader number="3" label="Rescheduling Journeys" sublabel="Sessions where users moved an existing booking to a new slot" color="#6b45c8" />
    <JourneyCategory
      title="Rescheduling Journeys"
      description="Sessions where a user attempted to reschedule a booking"
      data={data.rescheduling}
      color="#6b45c8"
      icon={<SyncOutlined />}
      useRescheduleFunnel={true}
    />
    {/* Section 4 — Appointment Update: only rendered when the backend provides data */}
    {data.appointment_update?.total > 0 && (
      <>
        <SectionHeader number="4" label="Appointment Update Journeys" sublabel="Sessions where a user updated an existing appointment" color={AU_COLOR} />
        <div className="ar-card ar-section">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: `${AU_COLOR}18`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <SyncOutlined style={{ color: AU_COLOR, fontSize: 18 }} />
              </div>
              <div style={{ textAlign: 'left' }}>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>Appointment Update Journeys</div>
                <div style={{ fontSize: 12, color: '#6b7280' }}>Sessions where a user updated an existing appointment</div>
              </div>
            </div>
            <CRBadge cr={data.appointment_update.cr} />
          </div>
          <AppointmentUpdateContent data={data.appointment_update} showDaily={true} isSplit={false} />
        </div>
      </>
    )}
  </>
);

// ─── ROOT COMPONENT ───────────────────────────────────────────────────────────
const AnalyticsReport = () => {
  const [loading7,    setLoading7]    = useState(true);
  const [loading30,   setLoading30]   = useState(true);
  const [loadingFull, setLoadingFull] = useState(false);
  const [error,       setError]       = useState(null);
  const [weeklyData,  setWeeklyData]  = useState(null);   // last 7 days
  const [monthlyData, setMonthlyData] = useState(null);   // last 30 days
  const [customData,  setCustomData]  = useState(null);   // custom range
  const [isCustomRange, setIsCustomRange] = useState(false);
  const [filters, setFilters] = useState({ date_from: null, date_to: null });
  const [pickerValue, setPickerValue] = useState(null);   // controlled RangePicker value (dayjs pair)
  const [isSyncing, setIsSyncing] = useState(false);

  // Load both 7-day and 30-day data in parallel on mount
  const loadDefaultData = useCallback(async () => {
    setLoading7(true);
    setLoading30(true);
    setError(null);
    const [res7, res30] = await Promise.allSettled([
      fetchAnalytics({ date_from: fmt(last7Start()),  date_to: fmt(yesterday()) }),
      fetchAnalytics({ date_from: fmt(last30Start()), date_to: fmt(yesterday()) }),
    ]);
    if (res7.status === 'fulfilled')  { setWeeklyData(res7.value); }
    else { console.error('7-day fetch failed:', res7.reason); }
    if (res30.status === 'fulfilled') { setMonthlyData(res30.value); }
    else { console.error('30-day fetch failed:', res30.reason); }
    if (res7.status === 'rejected' && res30.status === 'rejected') {
      setError('Failed to load analytics data. Please check your connection or try again.');
    }
    setLoading7(false);
    setLoading30(false);
  }, []);

  // Load custom range data (full-width)
  const loadCustom = useCallback(async (f) => {
    setLoadingFull(true);
    setError(null);
    try {
      const result = await fetchAnalytics(f);
      setCustomData(result);
    } catch (e) {
      console.error('Analytics custom range fetch failed:', e);
      setError('Failed to load analytics data for the selected range.');
    } finally {
      setLoadingFull(false);
    }
  }, []);

  useEffect(() => { loadDefaultData(); }, [loadDefaultData]);

  const handleRangeChange = useCallback((dates, dateFrom, dateTo) => {
    // Atomic: both dates and dayjs objects set together
    const next = { date_from: dateFrom, date_to: dateTo };
    setPickerValue(dates);     // keep picker display in sync
    setFilters(next);
    setIsCustomRange(true);
    loadCustom(next);
  }, [loadCustom]);

  const handleFilterChange = (key, value) => {
    const next = { ...filters, [key]: value };
    setFilters(next);
    if (next.date_from && next.date_to) {
      setIsCustomRange(true);
      loadCustom(next);
    }
  };

  const handleClearRange = useCallback(() => {
    setPickerValue(null);          // reset picker display
    setIsCustomRange(false);       // switch back to 2-column SplitView
    setCustomData(null);
    setFilters({ date_from: null, date_to: null });
  }, []);

  const [exporting, setExporting] = useState(false);

  const handleExport = useCallback(() => {
    // Pick the best available data: custom range > 30-day monthly > 7-day weekly
    const exportData = customData || monthlyData || weeklyData;
    if (!exportData) return;
    setExporting(true);
    try {
      // Build a human-readable label for the CSV header
      // NOTE: meta.date_from/to can be datetime strings ("YYYY-MM-DD HH:mm:ss") — strip time
      const stripTime = (d) => (d || '').split(' ')[0];
      const fmtLabel  = (d) => stripTime(d).split('-').reverse().join('/');
      let label = 'Last 30 Days';
      if (isCustomRange && filters.date_from && filters.date_to) {
        label = `${fmtLabel(filters.date_from)} – ${fmtLabel(filters.date_to)}`;
      } else if (weeklyData && !monthlyData) {
        label = 'Last 7 Days';
      }
      exportAnalyticsCSV(exportData, label);
    } finally {
      setExporting(false);
    }
  }, [customData, monthlyData, weeklyData, isCustomRange, filters]);

  const checkStatus = useCallback(async () => {
    try {
      const res = await fetchSyncStatus();
      if (res?.status === 'running') {
        setIsSyncing(true);
      } else if (res?.status === 'completed') {
        setIsSyncing((prevIsSyncing) => {
          if (prevIsSyncing) {
            message.success('Data synced successfully!');
            if (isCustomRange) {
              loadCustom(filters);
            } else {
              loadDefaultData();
            }
          }
          return false;
        });
      }
    } catch (e) {
      console.error('Failed to check sync status:', e);
    }
  }, [isCustomRange, filters, loadCustom, loadDefaultData]);

  useEffect(() => {
    // Initial check on mount
    checkStatus();
    
    let pollInterval;
    if (isSyncing) {
      pollInterval = setInterval(checkStatus, 5000);
    }
    return () => {
      if (pollInterval) clearInterval(pollInterval);
    };
  }, [isSyncing, checkStatus]);

  const handleSync = async () => {
    setIsSyncing(true);
    message.info('Data sync initiated. This may take a few moments.');
    try {
      await triggerSync();
      // Polling will handle the completion state
    } catch (e) {
      console.error('Sync failed:', e);
      message.error('Failed to initiate sync. Please try again.');
      setIsSyncing(false);
    }
  };

  const handlePrint = useCallback(() => {
    // ── Build print header ───────────────────────────────────────────────────
    const header = document.getElementById('ar-print-header');
    if (header) {
      let rangeLabel = 'Last 30 Days';
      let dateFromDisplay = fmtDisplay(last30Start());
      let dateToDisplay   = fmtDisplay(yesterday());
      if (isCustomRange && filters.date_from && filters.date_to) {
        const fmtD = (d) => d.split('-').reverse().join('/');
        dateFromDisplay = fmtD(filters.date_from);
        dateToDisplay   = fmtD(filters.date_to);
        rangeLabel = `${dateFromDisplay} \u2013 ${dateToDisplay}`;
      } else if (!isCustomRange) {
        // Split view: show both ranges
        const w7Start  = fmtDisplay(last7Start());
        const w30Start = fmtDisplay(last30Start());
        const yest     = fmtDisplay(yesterday());
        rangeLabel = `7-day: ${w7Start} \u2013 ${yest}  |  30-day: ${w30Start} \u2013 ${yest}`;
        dateFromDisplay = w30Start;
        dateToDisplay   = yest;
      }
      header.innerHTML = `
        <div class="ar-print-header__org">London Doctors Clinic &mdash; Part of Spire Healthcare</div>
        <div class="ar-print-header__title">Booking Analytics Report</div>
        <div class="ar-print-header__meta">
          <span><strong>Report Period:</strong> ${dateFromDisplay} &ndash; ${dateToDisplay}</span>
          <span><strong>View:</strong> ${rangeLabel}</span>
          <span><strong>Printed:</strong> ${new Date().toLocaleString('en-GB')}</span>
        </div>
      `;
    }

    // ── For split view (7-day + 30-day), reorder into a single column ────────
    // We swap .ar-split-layout to single-column and mark left column as "printed first"
    // by adding a CSS class that @media print uses to page-break between them.
    if (!isCustomRange) {
      document.querySelectorAll('.ar-split-layout').forEach(el => {
        el.classList.add('ar-print-stacked');
      });
    }

    const afterPrint = () => {
      // Restore screen layout
      document.querySelectorAll('.ar-split-layout.ar-print-stacked').forEach(el => {
        el.classList.remove('ar-print-stacked');
      });
      window.removeEventListener('afterprint', afterPrint);
    };
    window.addEventListener('afterprint', afterPrint);
    window.print();
  }, [isCustomRange, filters, monthlyData, weeklyData]);

  const activeData = isCustomRange ? customData : (monthlyData || weeklyData);
  const lastSyncedAt = activeData?.meta?.last_synced_at || null;
  // from_cache: backend sends meta.cached (bool) or top-level from_cache (string)
  const fromCache = activeData?.meta?.cached === true;

  return (
    <div className="ar-page">
      {isSyncing && (
        <Alert
          message="Analytics data sync is currently in progress. Dashboard data will refresh automatically when complete."
          type="info"
          showIcon
          icon={<SyncOutlined spin />}
          banner
          style={{ marginBottom: 16, borderRadius: 8, border: '1px solid #91caff' }}
        />
      )}
      <Toolbar
        filters={filters}
        pickerValue={pickerValue}
        onRangeChange={handleRangeChange}
        isCustomRange={isCustomRange}
        onClearRange={handleClearRange}
        onExport={handleExport}
        exporting={exporting}
        onPrint={handlePrint}
        onSync={handleSync}
        isSyncing={isSyncing}
        lastSyncedAt={lastSyncedAt}
        fromCache={fromCache}
      />

      {/* Print-only header — hidden on screen, visible when @media print ── */}
      <div id="ar-print-header" className="ar-print-header" aria-hidden="true" />

      {error && (
        <Alert
          type="error" message={error} showIcon style={{ marginBottom: 24 }}
          action={
            <button
              onClick={() => isCustomRange ? loadCustom(filters) : loadDefaultData()}
              style={{ border: 'none', background: 'none', color: '#008385', fontWeight: 600, cursor: 'pointer' }}
            >
              Retry
            </button>
          }
        />
      )}

      {isCustomRange ? (
        /* ── CUSTOM DATE RANGE: full-width single view ── */
        loadingFull && !customData ? (
          /* First load — show skeleton */
          <Skeleton active paragraph={{ rows: 18 }} />
        ) : (
          /* Subsequent fetches — show spinner overlay over existing data */
          <div style={{ position: 'relative' }}>
            {loadingFull && (
              <div style={{
                position: 'absolute', inset: 0, zIndex: 10,
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                justifyContent: 'center', gap: 12,
                background: 'rgba(255,255,255,0.75)',
                borderRadius: 12,
                backdropFilter: 'blur(2px)',
                minHeight: 120,
              }}>
                <div style={{
                  width: 36, height: 36, border: '3px solid #e5e7eb',
                  borderTopColor: '#008385', borderRadius: '50%',
                  animation: 'ar-spin 0.7s linear infinite',
                }} />
                <span style={{ fontSize: 13, color: '#6b7280', fontWeight: 500 }}>Loading…</span>
              </div>
            )}
            {customData ? (
              <FullView data={customData} />
            ) : !loadingFull ? (
              <Empty description="No analytics data for selected range" style={{ margin: '60px 0' }} />
            ) : null}
          </div>
        )
      ) : (
        /* ── DEFAULT: split 7-day left / 30-day right ── */
        <SplitView
          weeklyData={weeklyData}
          monthlyData={monthlyData}
          loading7={loading7}
          loading30={loading30}
        />
      )}
    </div>
  );
};

export default AnalyticsReport;
